<script lang="ts">
    import { twMerge } from 'tailwind-merge';
    import type { ButtonSize, ButtonTheme } from './button-types';

    export let theme: ButtonTheme = 'dark-green-inverted';
    export let size: ButtonSize = 'md';

    let className = '';

    export { className as class };

    export let href: string | undefined = undefined;

    export let buttonElement: HTMLButtonElement | undefined = undefined;
    export let disabled: boolean = false;

    let buttonClasses = `${
        href !== undefined ? 'inline-flex' : 'flex'
    } relative items-center gap-2 disabled:cursor-not-allowed`;

    /* Colors */
    switch (theme) {
        case 'dark-green-inverted':
            buttonClasses +=
                ' border border-prevent-primary-dark-green-100 text-prevent-primary-dark-green-100 aria-expanded:bg-prevent-primary-dark-green-100 aria-expanded:text-white';

            buttonClasses +=
                ' disabled:bg-prevent-primary-taupe-20 disabled:text-bodyText disabled:border disabled:border-[#887C7C] disabled:border-solid';
            break;
        case 'desert':
            buttonClasses +=
                ' border border-prevent-background-desert bg-prevent-background-desert text-bodyText';

            buttonClasses +=
                ' disabled:bg-prevent-primary-taupe-20 disabled:text-bodyText disabled:border disabled:border-[#887C7C] disabled:border-solid';
            break;
        case 'primary-green':
        case 'primary':
            buttonClasses +=
                ' border border-prevent-primary-green-100 bg-prevent-primary-green-100 text-white';

            buttonClasses +=
                ' disabled:bg-prevent-background-disabled disabled:text-bodyText disabled:border-none';
            break;
        case 'primary-green-inverted':
        case 'secondary':
            buttonClasses +=
                ' border-2 border-prevent-primary-green-100 bg-white text-prevent-primary-green-100 hover:bg-[#F3FAF7]';

            buttonClasses +=
                ' disabled:bg-prevent-background-disabled disabled:text-bodyText disabled:border-none';
            break;
        case 'select-input':
            buttonClasses +=
                ' border bg-gray-50 text-gray-900 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 border-[#869898]';

            buttonClasses +=
                ' disabled:bg-prevent-primary-taupe-20 disabled:text-bodyText disabled:border disabled:border-[#887C7C] disabled:border-solid';
            break;
        case 'danger':
            buttonClasses +=
                ' border border-prevent-red-contrast bg-prevent-red-contrast text-white';

            buttonClasses +=
                ' disabled:bg-prevent-primary-taupe-20 disabled:text-bodyText disabled:border disabled:border-[#887C7C] disabled:border-solid';
            break;
        case 'danger-inverted':
            buttonClasses +=
                ' border border-prevent-red-contrast bg-transparent text-prevent-red-contrast';

            buttonClasses +=
                ' disabled:bg-prevent-primary-taupe-20 disabled:text-bodyText disabled:border disabled:border-[#887C7C] disabled:border-solid';
            break;
        case 'tertiary':
            buttonClasses +=
                ' border-2 border-transparent bg-transparent text-prevent-primary-link hover:text-bodyText disabled:text-prevent-secondary-text';
            break;
    }

    /* Size */
    switch (size) {
        case 'none':
            buttonClasses += ' rounded font-bold';
            break;
        case 'xs':
            buttonClasses += ' rounded text-xs font-bold px-4';
            break;

        case 'sm':
            buttonClasses += ' rounded text-xs font-bold px-4 py-1';
            break;

        case 'md':
            buttonClasses += ' rounded text-base font-bold px-6 py-2';
            break;
        case 'lg':
            buttonClasses += ' rounded text-lg font-bold px-8 py-3';
            break;
        case 'full':
            buttonClasses += ' rounded-lg text-base font-bold px-6 py-2.5 w-full';
            break;
    }
</script>

<!--
@component
Display a button styled link or button

### Props
@prop `class` - (string): Tailwind classes to be applied to the button, merges with the default classes.
@prop `theme` - (ButtonTheme): The theme of the button, default is `dark-green-inverted`.
@prop `size` - (ButtonSize): The size of the button, default is `md`.
@prop `href` - (string): The href of the button, if defined the button will be a link.

### Example
```svelte
<Button theme="primary-green" size="lg">Primary green button</Button>
```
-->

{#if href !== undefined}
    <!-- svelte-ignore a11y-missing-attribute -->
    <a class={twMerge(buttonClasses, className)} {href} {...$$restProps}>
        <slot />
    </a>
{:else}
    <button
        bind:this={buttonElement}
        class={twMerge(buttonClasses, className)}
        {disabled}
        {...$$restProps}
        on:click
        on:keypress>
        <slot />
    </button>
{/if}
